import { useMutation } from '@tanstack/react-query'

import { postRequest } from 'src/lib/api-client'
import type { LeadDistributionPreference } from 'src/shared/features/enterprise/api/use-update-enterprise-settings'

export interface EnterpriseBusinessImportContinueParams {
  enterpriseId: number
  uploadId: string
  lynkEnabledForFile?: boolean | null
  containsClosedBusinesses?: boolean | null
  distributionSystemId?: number | null
  leadDistributionPreference?: LeadDistributionPreference | null
}

export interface EnterpriseBusinessImportContinueSummary {
  total: number
  created: number
  updated: number
  skippedDuplicates: number
  failed: number
  skippedSensitive: number
}

export interface EnterpriseBusinessImportContinueRow {
  rowNumber: number
  status: string
  businessId?: number
  resolvedReceiverUserId?: number | null
  externalLoanSource?: string | null
  externalLoanId?: string | null
  error?: string
  warnings?: string[]
}

export interface EnterpriseBusinessImportContinueResponse {
  success: boolean
  status: 'queued' | 'running' | 'completed' | 'failed' | 'canceled'
  summary: EnterpriseBusinessImportContinueSummary
  rows: EnterpriseBusinessImportContinueRow[]
  uploadId: string
  lynkEnabledForFile: boolean
  closedAtColumnExists: boolean
  detectedClosedAtColumns: string[]
  containsClosedBusinesses: boolean | null | undefined
  distributionSystemId: number | null
  leadDistributionPreference: LeadDistributionPreference | null
}

/**
 * Continue a staged enterprise business import using the preview upload id and import options.
 */
const continueEnterpriseBusinessImport = async ({
  enterpriseId,
  uploadId,
  lynkEnabledForFile,
  containsClosedBusinesses,
  distributionSystemId,
  leadDistributionPreference,
}: EnterpriseBusinessImportContinueParams) => {
  const searchParams = new URLSearchParams()

  if (lynkEnabledForFile !== undefined && lynkEnabledForFile !== null) {
    searchParams.append('lynk_enabled_for_file', String(lynkEnabledForFile))
  }

  if (containsClosedBusinesses !== undefined && containsClosedBusinesses !== null) {
    searchParams.append('contains_closed_businesses', String(containsClosedBusinesses))
  }

  if (distributionSystemId !== undefined && distributionSystemId !== null) {
    searchParams.append('distribution_system_id', String(distributionSystemId))
  }

  if (leadDistributionPreference !== undefined && leadDistributionPreference !== null) {
    searchParams.append('lead_distribution_preference', leadDistributionPreference)
  }

  const queryString = searchParams.toString()
  const url = `/enterprise/${enterpriseId}/business-import/${uploadId}/continue${queryString ? `?${queryString}` : ''}`

  return postRequest<EnterpriseBusinessImportContinueResponse>(url)
}

export default function useEnterpriseBusinessImportContinue() {
  return useMutation({
    mutationFn: async (params: EnterpriseBusinessImportContinueParams) => {
      const response = await continueEnterpriseBusinessImport(params)

      if (!response) {
        throw new Error('No response received from enterprise business import continue')
      }

      return response
    },
  })
}
